import { Helmet } from "react-helmet-async";
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
} from "react-router-dom";

import { Suspense, lazy } from "react";

// Common Components
import LoadTop from "../components/Common/ScrollTop/LoadTop";
import FloatingContact from "../components/FloatingContact/FloatingContact";

// Global Styles
import "../assets/scss/critical.scss";
import "../assets/scss/App.scss";

// ================= LAZY PAGES =================

const Home = lazy(() => import("../pages/home"));
const About = lazy(() => import("../pages/about"));
const Projects = lazy(() => import("../pages/Products"));
const Services = lazy(() => import("../pages/service"));
const Contact = lazy(() => import("../pages/contact"));
const Error = lazy(() => import("../pages/404"));

// Blog
const Blog = lazy(() => import("../pages/Blog/Blog"));
const BlogPost = lazy(() => import("../pages/Blog/BlogPost"));

// Main Services
const WebDevelopment = lazy(() =>
  import("../pages/service/analytic-solutions")
);

const BrandIdentity = lazy(() =>
  import("../pages/service/BrandIdentity/brand-identity")
);

const DigitalMarketing = lazy(() =>
  import("../pages/service/DigitalMarketing/digital-marketing")
);

const MachineLearning = lazy(() =>
  import("../pages/service/machine-learning")
);

const ServiceDetails = lazy(() =>
  import("../pages/service/service-details")
);

const WebDesign = lazy(() =>
  import("../pages/service/web-development")
);

const WordpressDevelopment = lazy(() =>
  import("../pages/service/WordPress/wordpress-development")
);

// Digital Marketing
const Seo = lazy(() =>
  import("../pages/service/DigitalMarketing/seo/seo")
);

const Sem = lazy(() =>
  import("../pages/service/DigitalMarketing/sem/sem")
);

const Smm = lazy(() =>
  import("../pages/service/DigitalMarketing/smm/smm")
);

const Content = lazy(() =>
  import("../pages/service/DigitalMarketing/content/content")
);

const Content1 = lazy(() =>
  import("../pages/service/content/content")
);

const Email = lazy(() =>
  import("../pages/service/DigitalMarketing/email/email")
);

const Whatsapp = lazy(() =>
  import("../pages/service/DigitalMarketing/whatsapp/whatsapp")
);

// Web Design Sub Services
const SocialMediaDesign = lazy(() =>
  import("../pages/service/social-media-design")
);

const WebDesignuiuxdev = lazy(() =>
  import("../pages/service/web-designs-ui-ux")
);

const BusinessWebsite = lazy(() =>
  import("../pages/service/web-development-busweb")
);

const ECommerceWebSite = lazy(() =>
  import("../pages/service/web-development-ews")
);

const Reactjswebsitedevelopment = lazy(() =>
  import("../pages/service/web-development-reactwd")
);

const WooCommerce = lazy(() =>
  import("../pages/service/web-development-woo")
);

const WordpressDev = lazy(() =>
  import("../pages/service/web-development-wp")
);

const Logodesign = lazy(() =>
  import("../pages/service/web-developmentlogo-design")
);

// Portfolio
const ProjectOne = lazy(() =>
  import("../pages/Portfolio/project-one")
);

const ProjectTwo = lazy(() =>
  import("../pages/Portfolio/project-two")
);

const ProjectThree = lazy(() =>
  import("../pages/Portfolio/project-three")
);

const CaseStudtyStyleOne = lazy(() =>
  import("../pages/Portfolio/style-1")
);

const CaseStudtyStyleTwo = lazy(() =>
  import("../pages/Portfolio/style-2")
);

const CaseStudtyStyleThree = lazy(() =>
  import("../pages/Portfolio/style-3")
);

const CaseStudtyStyleFour = lazy(() =>
  import("../pages/Portfolio/style-4")
);

const CaseStudtyStyleFive = lazy(() =>
  import("../pages/Portfolio/style-5")
);

const CaseStudtyStyleSix = lazy(() =>
  import("../pages/Portfolio/style-6")
);

// ================= LOADING UI =================

const PageLoader = () => (
  <div
    style={{
      minHeight: "50vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
    aria-label="Loading page"
  >
    <div
      className="spinner-border text-primary"
      role="status"
      aria-hidden="true"
    />
  </div>
);

// ================= ROOT LAYOUT =================

const RootLayout = () => (
  <>
    <Helmet>

      <title>
        Sadhvi Software Solutions | Software Development Company in Coimbatore
      </title>

      <meta
        name="description"
        content="Sadhvi Software Solutions is a leading software development company in Coimbatore specializing in web design, digital marketing, and business automation."
      />

      <meta
        name="keywords"
        content="Software Development, Web Design, Digital Marketing, SEO, Coimbatore"
      />

      <meta
        name="robots"
        content="index, follow"
      />

      {/* Open Graph */}
      <meta
        property="og:title"
        content="Sadhvi Software Solutions | Software Development Company in Coimbatore"
      />

      <meta
        property="og:description"
        content="Sadhvi Software Solutions builds custom websites, mobile apps, and digital marketing strategies that help businesses grow online."
      />

      <meta
        property="og:type"
        content="website"
      />

      <meta
        property="og:url"
        content="https://sadhvisoft.com/"
      />

      <meta
        property="og:image"
        content="https://sadhvisoft.com/images/sadhvisoft-og-image.jpg"
      />

      <meta
        property="og:site_name"
        content="Sadhvi Software Solutions"
      />

      {/* Twitter */}
      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content="Sadhvi Software Solutions | Software Development Company in Coimbatore"
      />

      <meta
        name="twitter:description"
        content="Transform your business with our expert web development, branding, and marketing solutions in Coimbatore."
      />

      <meta
        name="twitter:image"
        content="https://sadhvisoft.com/images/sadhvisoft-og-image.jpg"
      />

      <meta
        name="twitter:site"
        content="@sadhvisoft"
      />

    </Helmet>

    <FloatingContact />

    <LoadTop />

    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  </>
);

// ================= ROUTER =================

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,

    children: [

      // Home
      {
        index: true,
        element: <Home />,
      },

      {
        path: "home",
        element: <Home />,
      },

      // About
      {
        path: "about",
        element: <About />,
      },

      // Services
      {
        path: "service",
        element: <Services />,
      },

      {
        path: "projects",
        element: <Projects />,
      },

      {
        path: "contact",
        element: <Contact />,
      },

      // ================= BLOG =================

      {
        path: "blog",
        element: <Blog />,
      },

      {
        path: "blog/:slug",
        element: <BlogPost />,
      },

      // ================= WEB DESIGN =================

      {
        path: "service/web-design/logo-design",
        element: <Logodesign />,
      },

      {
        path: "service/web-design/social-media-design",
        element: <SocialMediaDesign />,
      },

      {
        path: "service/web-design/web-designs-ui-ux",
        element: <WebDesignuiuxdev />,
      },

      {
        path: "service/web-development/wordpress-website-development",
        element: <WordpressDev />,
      },

      {
        path: "service/web-development/WooCommerce-website-development",
        element: <WooCommerce />,
      },

      {
        path: "service/web-development/business-website-development",
        element: <BusinessWebsite />,
      },

      {
        path: "service/web-development/ecommerc-website-development",
        element: <ECommerceWebSite />,
      },

      {
        path: "service/web-development/reactjs-website-development",
        element: <Reactjswebsitedevelopment />,
      },

      // ================= MAIN SERVICES =================

      {
        path: "service/service-details",
        element: <ServiceDetails />,
      },

      {
        path: "service/web-design",
        element: <WebDesign />,
      },

      {
        path: "service/web-development",
        element: <WebDevelopment />,
      },

      {
        path: "service/wordpress-development",
        element: <WordpressDevelopment />,
      },

      {
        path: "service/analytic-solutions",
        element: <WebDevelopment />,
      },

      {
        path: "service/brand-identity",
        element: <BrandIdentity />,
      },

      {
        path: "service/machine-learning",
        element: <MachineLearning />,
      },

      {
        path: "service/DigitalMarketing/digital-marketing",
        element: <DigitalMarketing />,
      },

      // ================= DIGITAL MARKETING =================

      {
        path: "service/DigitalMarketing/seo",
        element: <Seo />,
      },

      {
        path: "service/DigitalMarketing/sem",
        element: <Sem />,
      },

      {
        path: "service/DigitalMarketing/smm",
        element: <Smm />,
      },

      {
        path: "service/DigitalMarketing/content",
        element: <Content />,
      },

      {
        path: "service/AnalyticsAndReport",
        element: <Content1 />,
      },

      {
        path: "service/DigitalMarketing/email",
        element: <Email />,
      },

      {
        path: "service/DigitalMarketing/whatsapp",
        element: <Whatsapp />,
      },

      // ================= PORTFOLIO =================

      {
        path: "case-study/style-1",
        element: <CaseStudtyStyleOne />,
      },

      {
        path: "case-study/style-2",
        element: <CaseStudtyStyleTwo />,
      },

      {
        path: "case-study/style-3",
        element: <CaseStudtyStyleThree />,
      },

      {
        path: "case-study/style-4",
        element: <CaseStudtyStyleFour />,
      },

      {
        path: "case-study/style-5",
        element: <CaseStudtyStyleFive />,
      },

      {
        path: "case-study/style-6",
        element: <CaseStudtyStyleSix />,
      },

      {
        path: "Portfolio/project-one",
        element: <ProjectOne />,
      },

      {
        path: "Portfolio/project-two",
        element: <ProjectTwo />,
      },

      {
        path: "Portfolio/project-three",
        element: <ProjectThree />,
      },

      // ================= 404 =================

      {
        path: "*",
        element: <Error />,
      },
    ],
  },
]);

// ================= APP =================

const App = () => {
  return (
    <div className="App">
      <RouterProvider router={router} />
    </div>
  );
};

export default App;